import { useEffect, useState } from 'react';
import { Loader2, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { fetchBadgePdfBlob, printBadgePdf } from '@/lib/badges';

type BadgePreviewDialogProps = {
  userId: string | null;
  userName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function BadgePreviewDialog({
  userId,
  userName,
  open,
  onOpenChange,
}: BadgePreviewDialogProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!open || !userId) {
      setPreviewUrl(null);
      return;
    }

    let cancelled = false;
    let urlToRevoke: string | null = null;

    setLoading(true);
    fetchBadgePdfBlob([userId])
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        urlToRevoke = url;
        setPreviewUrl(url);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load badge preview';
        toast.error(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [open, userId]);

  async function handlePrint() {
    if (!userId) return;
    setPrinting(true);
    try {
      await printBadgePdf([userId]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send badge to printer';
      toast.error(message);
    } finally {
      setPrinting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Badge preview{userName ? ` — ${userName}` : ''}</DialogTitle>
          <DialogDescription>
            Review the badge below, then send it to the printer.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center bg-muted/30 rounded-md border p-4 min-h-[260px]">
          {loading || !previewUrl ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading preview…</span>
            </div>
          ) : (
            <iframe
              title="Badge preview"
              src={previewUrl}
              className="border bg-white"
              style={{ width: 480, height: 360 }}
            />
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={printing}>
            Close
          </Button>
          <Button onClick={handlePrint} disabled={!userId || loading || printing}>
            {printing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Opening printer…
              </>
            ) : (
              <>
                <Printer className="h-4 w-4 mr-2" />
                Print Badge
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
